// S12 — the plans manager (spec 15 §4; M15-6, D-486, D-615). Every role reads
// the list; platform_admin creates, edits and retires. A plan's price is
// served as null to support and readonly (§7), so the column reads "hidden"
// for them rather than a made-up amount. A sandbox member edits sandbox
// plans only, and the editor makes a sandbox member's new plan a sandbox one
// (the server decides; the console only stops offering what it would refuse).

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ENTITLEMENT_MODULE_CODES, isKnownCurrency } from "@assessly/contracts";
import { PageHeader } from "../../../components/breadcrumbs";
import { Button } from "../../../components/button";
import { DataTable, type DataTableColumn } from "../../../components/data-table";
import { CheckboxField, Field } from "../../../components/field";
import { Modal } from "../../../components/modal";
import { EmptyState, ErrorState, TableSkeleton } from "../../../components/states";
import { StatusChip } from "../../../components/status-chip";
import { Banner } from "../../../components/toast";
import { requestIdOf, useSignInAgainOn401 } from "../components/console-frame";
import { useHeldKey, withHeldKey, type HeldKey } from "../lib/held-key";
import { packLabel } from "../lib/labels";
import { listVerticalPacks, type PlatformMe } from "../lib/platform-api";
import {
  createPlan,
  listPlans,
  retirePlan,
  updatePlan,
  type PlanCreateInput,
  type PlanLimitCode,
  type PlanView,
} from "../lib/plans-api";
import {
  canEditPlan,
  canRetirePlan,
  canEditPlans,
  formatCount,
  formatMinor,
  majorFromMinor,
  moduleLabel,
  planFailure,
  toMinor,
} from "../lib/plans";

export const PLANS_QUERY = ["platform", "plans"] as const;

const LIMIT_CODES: readonly PlanLimitCode[] = [
  "max_facilities",
  "max_users_soft",
  "storage_gb",
  "api_requests_day",
];

/** The banner for a refused plan write. */
export function planMessage(error: unknown, t: TFunction): string {
  switch (planFailure(error)) {
    case "scope":
      return t("admin.plans.refusedScope");
    case "role":
      return t("admin.tenant.refusedRole");
    case "code_taken":
      return t("admin.plans.codeTaken");
    case "retired":
      return t("admin.plans.retiredEdit");
    case "pilot":
      return t("admin.plans.pilotRequired");
    case "thresholds":
      return t("admin.plans.thresholds");
    default:
      return t("admin.plans.failed");
  }
}

export function PlansScreen({ me }: { me: PlatformMe }) {
  const { t, i18n } = useTranslation();
  const [showRetired, setShowRetired] = useState(false);
  const [editing, setEditing] = useState<PlanView | "new" | null>(null);
  const [retiring, setRetiring] = useState<PlanView | null>(null);
  const plans = useQuery({
    queryKey: [...PLANS_QUERY, { showRetired }],
    queryFn: ({ signal }) => listPlans(showRetired, signal),
  });
  useSignInAgainOn401(plans.error);
  const editor = canEditPlans(me.role);

  const columns: DataTableColumn<PlanView>[] = [
    {
      key: "name",
      header: t("admin.plans.colName"),
      render: (row) => (
        <span className="admin-name-cell">
          <span className="admin-strong">{row.name}</span>
          {row.sandbox ? (
            <StatusChip variant="accent" dot={false}>
              {t("admin.plans.sandbox")}
            </StatusChip>
          ) : null}
        </span>
      ),
    },
    {
      key: "code",
      header: t("admin.plans.colCode"),
      id: true,
      render: (row) => <code>{row.code}</code>,
    },
    {
      key: "status",
      header: t("admin.plans.colStatus"),
      render: (row) => (
        <StatusChip
          variant={row.status === "active" ? "ok" : "neutral"}
          data-testid="admin-plan-status"
        >
          {t(`admin.plans.status.${row.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "price",
      header: t("admin.plans.colPrice"),
      render: (row) =>
        row.annual_price_minor === null ? (
          <span className="admin-muted" data-testid="admin-plan-price-hidden">
            {t("admin.plans.priceHidden")}
          </span>
        ) : (
          formatMinor(row.annual_price_minor, row.currency, i18n.language)
        ),
    },
    {
      key: "tenants",
      header: t("admin.plans.colTenants"),
      render: (row) => formatCount(row.tenant_count, i18n.language),
    },
    {
      key: "modules",
      header: t("admin.plans.colModules"),
      render: (row) => formatCount(row.entitlements.filter((e) => e.enabled).length, i18n.language),
    },
    ...(editor
      ? [
          {
            key: "actions",
            header: t("admin.plans.colActions"),
            render: (row: PlanView) =>
              canEditPlan(me.role, me.scope, row) ? (
                <span className="admin-inline">
                  <Button
                    size="sm"
                    data-testid="admin-plan-edit"
                    onClick={() => {
                      setEditing(row);
                    }}
                  >
                    {t("admin.plans.edit")}
                  </Button>
                  {canRetirePlan(me.role, me.scope, row) ? (
                    <Button
                      size="sm"
                      variant="danger"
                      data-testid="admin-plan-retire"
                      onClick={() => {
                        setRetiring(row);
                      }}
                    >
                      {t("admin.plans.retire")}
                    </Button>
                  ) : null}
                </span>
              ) : null,
          },
        ]
      : []),
  ];

  let body;
  if (plans.isPending) {
    body = <TableSkeleton rows={4} cols={5} label={t("admin.plans.loading")} />;
  } else if (plans.isError) {
    body = (
      <ErrorState
        title={t("admin.load.failed")}
        retryLabel={t("admin.load.retry")}
        onRetry={() => {
          void plans.refetch();
        }}
        requestId={requestIdOf(plans.error)}
        requestIdLabel={t("admin.load.requestId")}
      />
    );
  } else if (plans.data.length === 0) {
    body = (
      <EmptyState
        title={t("admin.plans.emptyTitle")}
        body={editor ? t("admin.plans.emptyBodyAdmin") : t("admin.plans.emptyBody")}
        {...(editor
          ? {
              ctaLabel: t("admin.plans.create"),
              onCta: () => {
                setEditing("new");
              },
            }
          : {})}
      />
    );
  } else {
    body = (
      <DataTable
        columns={columns}
        rows={plans.data}
        rowKey={(row) => row.id}
        empty={null}
        caption={t("admin.plans.title")}
        rowTestId="admin-plan-row"
        rowProps={(row) => ({ "data-code": row.code })}
      />
    );
  }

  return (
    <section className="admin-screen" data-testid="admin-plans">
      <PageHeader
        title={t("admin.plans.title")}
        actions={
          editor ? (
            <Button
              variant="primary"
              size="sm"
              data-testid="admin-plan-create"
              onClick={() => {
                setEditing("new");
              }}
            >
              {t("admin.plans.create")}
            </Button>
          ) : undefined
        }
      />
      <div className="admin-filters">
        <CheckboxField
          label={t("admin.plans.showRetired")}
          checked={showRetired}
          onChange={setShowRetired}
          data-testid="admin-plans-show-retired"
        />
      </div>
      {body}
      {editing !== null ? (
        <PlanEditor
          plan={editing === "new" ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
      {retiring !== null ? (
        <RetireDialog
          plan={retiring}
          onClose={() => {
            setRetiring(null);
          }}
        />
      ) : null}
    </section>
  );
}

interface LimitDraft {
  on: boolean;
  value: string;
  warn: string;
  block: string;
}

interface PlanDraft {
  code: string;
  name: string;
  price: string;
  currency: string;
  dunningDays: string;
  notes: string;
  modules: Record<string, boolean>;
  limits: Record<PlanLimitCode, LimitDraft>;
}

function draftOf(plan: PlanView | null): PlanDraft {
  const modules: Record<string, boolean> = {};
  for (const code of ENTITLEMENT_MODULE_CODES) {
    modules[code] = plan?.entitlements.find((e) => e.module_code === code)?.enabled ?? false;
  }
  const limits = {} as Record<PlanLimitCode, LimitDraft>;
  for (const code of LIMIT_CODES) {
    const row = plan?.limits.find((l) => l.limit_code === code);
    limits[code] = {
      on: row !== undefined,
      value: row === undefined ? "" : String(row.limit_value),
      warn:
        row?.warn_pct === null || row === undefined
          ? code === "storage_gb"
            ? "80"
            : ""
          : String(row.warn_pct),
      block:
        row?.block_pct === null || row === undefined
          ? code === "storage_gb"
            ? "100"
            : ""
          : String(row.block_pct),
    };
  }
  return {
    code: plan?.code ?? "",
    name: plan?.name ?? "",
    price:
      plan?.annual_price_minor == null
        ? ""
        : majorFromMinor(plan.annual_price_minor, plan.currency),
    currency: plan?.currency ?? "USD",
    dunningDays: String(plan?.dunning_suspend_after_days ?? 30),
    notes: plan?.notes ?? "",
    modules,
    limits,
  };
}

const CODE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const WHOLE = /^\d+$/;

/** The request body a draft makes, or null while a field is not valid. */
export function planBodyOf(draft: PlanDraft, existing: PlanView | null): PlanCreateInput | null {
  const currency = draft.currency.trim().toUpperCase();
  const minor = toMinor(draft.price, currency);
  const days = Number(draft.dunningDays);
  if (draft.name.trim() === "" || minor === null || !isKnownCurrency(currency)) return null;
  if (!/^[A-Z]{3}$/.test(currency) || !Number.isInteger(days) || days < 7 || days > 90) return null;
  if (existing === null && (!CODE_PATTERN.test(draft.code) || draft.code.length < 2)) return null;
  const limits: PlanCreateInput["limits"] = [];
  for (const code of LIMIT_CODES) {
    const l = draft.limits[code];
    if (!l.on) continue;
    if (!WHOLE.test(l.value)) return null;
    if (code === "storage_gb") {
      if (!WHOLE.test(l.warn) || !WHOLE.test(l.block) || Number(l.block) <= Number(l.warn)) {
        return null;
      }
      limits.push({
        limit_code: code,
        limit_value: Number(l.value),
        warn_pct: Number(l.warn),
        block_pct: Number(l.block),
      });
    } else {
      limits.push({ limit_code: code, limit_value: Number(l.value) });
    }
  }
  return {
    code: existing?.code ?? draft.code,
    name: draft.name.trim(),
    annual_price_minor: minor,
    currency,
    dunning_suspend_after_days: days,
    ...(draft.notes.trim() !== "" ? { notes: draft.notes.trim() } : {}),
    entitlements: ENTITLEMENT_MODULE_CODES.map((module_code) => ({
      module_code,
      enabled: draft.modules[module_code] ?? false,
    })),
    limits,
  };
}

function PlanEditor({ plan, onClose }: { plan: PlanView | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const heldKey: HeldKey = useHeldKey();
  const [draft, setDraft] = useState<PlanDraft>(() => draftOf(plan));
  const packs = useQuery({
    queryKey: ["platform", "vertical-packs"],
    queryFn: ({ signal }) => listVerticalPacks(signal),
    staleTime: 5 * 60_000,
  });
  const body = useMemo(() => planBodyOf(draft, plan), [draft, plan]);
  const priceHidden = plan !== null && plan.annual_price_minor === null;
  const save = useMutation({
    mutationFn: () => {
      if (body === null) throw new Error("invalid draft");
      if (plan === null) {
        return withHeldKey(heldKey, { create: body }, (key) => createPlan(body, key));
      }
      const { code: _code, ...patch } = body;
      const input = priceHidden ? (({ annual_price_minor: _p, ...rest }) => rest)(patch) : patch;
      return withHeldKey(heldKey, { update: plan.id, input }, (key) =>
        updatePlan(plan.id, input, key),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PLANS_QUERY });
      onClose();
    },
  });
  useSignInAgainOn401(save.error);

  function setLimit(code: PlanLimitCode, patch: Partial<LimitDraft>) {
    setDraft((d) => ({ ...d, limits: { ...d.limits, [code]: { ...d.limits[code], ...patch } } }));
  }

  const title =
    plan === null
      ? t("admin.plans.editorCreate")
      : t("admin.plans.editorEdit", { name: plan.name });
  return (
    <Modal
      label={title}
      heading={title}
      closeLabel={t("admin.plans.close")}
      onClose={onClose}
      pending={save.isPending}
      testId="admin-plan-editor"
      className="admin-modal admin-modal-wide"
    >
      <form
        className="admin-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (body !== null) save.mutate();
        }}
      >
        {plan !== null && plan.tenant_count > 0 ? (
          <Banner kind="neutral" data-testid="admin-plan-affects">
            {t("admin.plans.affects", { count: plan.tenant_count })}
          </Banner>
        ) : null}
        <div className="admin-form-row">
          <Field label={t("admin.plans.name")}>
            <input
              className="ds-input"
              value={draft.name}
              maxLength={200}
              data-testid="admin-plan-name"
              onChange={(e) => {
                setDraft({ ...draft, name: e.target.value });
              }}
            />
          </Field>
          <Field label={t("admin.plans.code")}>
            <input
              className="ds-input"
              value={draft.code}
              maxLength={40}
              spellCheck={false}
              disabled={plan !== null}
              data-testid="admin-plan-code"
              onChange={(e) => {
                setDraft({ ...draft, code: e.target.value.toLowerCase() });
              }}
            />
          </Field>
        </div>
        {plan === null ? <p className="admin-hint">{t("admin.plans.codeHint")}</p> : null}
        <div className="admin-form-row">
          {priceHidden ? null : (
            <Field label={t("admin.plans.price")}>
              <input
                className="ds-input"
                inputMode="decimal"
                value={draft.price}
                data-testid="admin-plan-price"
                onChange={(e) => {
                  setDraft({ ...draft, price: e.target.value });
                }}
              />
            </Field>
          )}
          <Field label={t("admin.plans.currency")}>
            <input
              className="ds-input"
              value={draft.currency}
              maxLength={3}
              spellCheck={false}
              data-testid="admin-plan-currency"
              onChange={(e) => {
                setDraft({ ...draft, currency: e.target.value.toUpperCase() });
              }}
            />
          </Field>
          <Field label={t("admin.plans.dunningDays")}>
            <input
              className="ds-input"
              inputMode="numeric"
              value={draft.dunningDays}
              data-testid="admin-plan-dunning"
              onChange={(e) => {
                setDraft({ ...draft, dunningDays: e.target.value });
              }}
            />
          </Field>
        </div>
        <fieldset className="admin-checks" data-testid="admin-plan-modules">
          <legend className="ds-field-label filter-chip-label">{t("admin.plans.modules")}</legend>
          {ENTITLEMENT_MODULE_CODES.map((code) => (
            <CheckboxField
              key={code}
              label={moduleLabel(code, t, (k) => packLabel(k, packs.data, i18n.language))}
              checked={draft.modules[code] ?? false}
              data-testid={`admin-plan-module-${code}`}
              onChange={(checked) => {
                setDraft({ ...draft, modules: { ...draft.modules, [code]: checked } });
              }}
            />
          ))}
        </fieldset>
        <fieldset className="admin-limits" data-testid="admin-plan-limits">
          <legend className="ds-field-label filter-chip-label">{t("admin.plans.limits")}</legend>
          {LIMIT_CODES.map((code) => {
            const l = draft.limits[code];
            // A limit row is set, never deleted: an existing one stays on.
            const locked = plan?.limits.some((x) => x.limit_code === code) ?? false;
            return (
              <div className="admin-limit-row" key={code} data-limit={code}>
                <CheckboxField
                  label={t(`admin.limitCodes.${code}`)}
                  checked={l.on}
                  disabled={locked}
                  data-testid={`admin-plan-limit-${code}`}
                  onChange={(checked) => {
                    setLimit(code, { on: checked });
                  }}
                />
                {l.on ? (
                  <>
                    <Field label={t("admin.plans.limitValue")}>
                      <input
                        className="ds-input"
                        inputMode="numeric"
                        value={l.value}
                        data-testid={`admin-plan-limit-${code}-value`}
                        onChange={(e) => {
                          setLimit(code, { value: e.target.value });
                        }}
                      />
                    </Field>
                    {code === "storage_gb" ? (
                      <>
                        <Field label={t("admin.plans.warnPct")}>
                          <input
                            className="ds-input"
                            inputMode="numeric"
                            value={l.warn}
                            onChange={(e) => {
                              setLimit(code, { warn: e.target.value });
                            }}
                          />
                        </Field>
                        <Field label={t("admin.plans.blockPct")}>
                          <input
                            className="ds-input"
                            inputMode="numeric"
                            value={l.block}
                            onChange={(e) => {
                              setLimit(code, { block: e.target.value });
                            }}
                          />
                        </Field>
                      </>
                    ) : null}
                  </>
                ) : null}
              </div>
            );
          })}
        </fieldset>
        <Field label={t("admin.plans.notes")} wide>
          <textarea
            className="ds-input"
            rows={2}
            maxLength={2000}
            value={draft.notes}
            onChange={(e) => {
              setDraft({ ...draft, notes: e.target.value });
            }}
          />
        </Field>
        {save.isError ? (
          <Banner kind="bad" data-testid="admin-plan-error">
            {planMessage(save.error, t)}
          </Banner>
        ) : null}
        <div className="admin-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={body === null}
            busy={save.isPending}
            busyLabel={t("admin.plans.saving")}
            data-testid="admin-plan-save"
          >
            {t("admin.plans.save")}
          </Button>
          <Button disabled={save.isPending} onClick={onClose}>
            {t("admin.plans.cancel")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function RetireDialog({ plan, onClose }: { plan: PlanView; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const heldKey = useHeldKey();
  const retire = useMutation({
    mutationFn: () => withHeldKey(heldKey, { retire: plan.id }, (key) => retirePlan(plan.id, key)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PLANS_QUERY });
      onClose();
    },
  });
  useSignInAgainOn401(retire.error);
  const title = t("admin.plans.retireTitle", { name: plan.name });
  return (
    <Modal
      label={title}
      heading={title}
      closeLabel={t("admin.plans.close")}
      onClose={onClose}
      pending={retire.isPending}
      testId="admin-plan-retire-dialog"
      className="admin-modal"
    >
      <p className="admin-muted">{t("admin.plans.retireBody")}</p>
      {retire.isError ? (
        <Banner kind="bad" data-testid="admin-plan-retire-error">
          {planMessage(retire.error, t)}
        </Banner>
      ) : null}
      <div className="admin-actions">
        <Button
          variant="danger"
          busy={retire.isPending}
          busyLabel={t("admin.plans.retiring")}
          data-testid="admin-plan-retire-confirm"
          onClick={() => {
            retire.mutate();
          }}
        >
          {t("admin.plans.retireConfirm")}
        </Button>
        <Button disabled={retire.isPending} onClick={onClose}>
          {t("admin.plans.cancel")}
        </Button>
      </div>
    </Modal>
  );
}
